/* eslint-disable new-cap,camelcase */
import BasePresenter from '@src/js/separate-logic/base-presenter';
import { reaction } from 'mobx';
import CommonTips from '@src/framework/frame/components/common-tips';
import { external } from '@src/js/separate-logic/decorate/external';
import PerformanceStats from '@com-pc/performance-stats';
import TimeRecord from '@src/slide/infrastructure/module/independent/time-record';
import { TDWActionType } from '@src/slide/infrastructure/module/dependent/report/offline-data-report/constant';
import config, { EnterPriseType } from '@src/js/service/enterprise/temp-config';
import { AppServiceProvider } from '@src/slide/application/app-service-provider';
import { MutationType } from '@tencent/slide-sdk-model';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { dispatchProps } from '@src/js/separate-logic/decorate/dispatchProps';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { IImageUploadService } from '@src/slide/application/service/image-upload-service/core/interface';
import { IDynamicService } from '@src/slide/infrastructure/module/independent/dynamic-loader/dynamic-service';
import { IHistoryManager } from '@src/slide/application/service/history-manager/core/history-manager.interface';
import { ILiveDropdownService } from '@src/slide/application/service/live-dropdown-service/core/interface';
import {
    IImportFileManager,
} from '@src/slide/application/service/import-file-manager/core/import-file-manager.interface';

export const UNDO_REDO_CHANGE = 'undo_redo_change';

declare global {
    interface Window {
        firstLoading: any;
    }
}

export default class ContainerPresenter extends BasePresenter {
    public init() {
        this.getAppServices().viewableServiceLayer.playManager.playAction = this.play;
        this.getAppServices().viewableServiceLayer.playManager.playEndAction = this.playEnd;
        this.disposeOnUnmount(reaction(
            () => this.getAppServices().getUiDataService().presentation.operation.loading,
            (loading) => {
                if (!loading) { // 所有页数PPT加载完毕
                    import(/* webpackChunkName: "service" */ '@src/slide/infrastructure/module/dependent/performance/slow-render/slow-render-collector').then((module) => {
                        const SlowRenderCollector = module.default;
                        SlowRenderCollector.getInstance().startSlowRenderMonitor();
                    })
                        .catch((error) => {
                            window.log.error(`[loadChunk-error]${error}`);
                        });
                    import(/* webpackChunkName: "service" */ '../../../../../../js/performance/presentationInfoCollector').then((module) => {
                        const PresentationInfoCollector = module.default;
                        PresentationInfoCollector.getInstance()
                            .collectElementInfo(this.getAppServices().getUiDataService().presentation.sdkData);
                    })
                        .catch((error) => {
                            window.log.error(`[loadChunk-error]${error}`);
                        });
                }
            },
        ));

        SlideDataServices.SDKDataServices.EventManager.promise().then(({ commandEvents }) => {
            commandEvents.onUndoRedoChange((res) => {
                const { mutationsType } = res;
                // 动画的编辑操作(如：修改动画类型、时间等)，无需触发ppt-resize
                if (this.checkIfAnimOperation(mutationsType)) {
                    return;
                }
                // undo redo后，去触发一次resize,保证页面的可滚动状态是对的
                requestAnimationFrame(() => {
                    window.dispatchEvent(new Event('PPT-resize'));
                });
            });
        });

        if (!config.supportEnterprise(EnterPriseType.ALL)) {
            setTimeout(() => {
                PerformanceStats.autoShow();
            }, 1000);
        }
    }

    public componentDidMount() {
        if (config.supportEnterprise(EnterPriseType.ALL)) {
            window.firstLoading?.hide();
        }
        window.dispatchEvent(new Event('resize'));
    }

    /**
     * 保留原有逻辑
     * 执行完 props.handleEndPlay 后调用内部方法
     * 更新状态
     */
    @external()
    public playEndInner = () => {
        CommonTips.getInstance().resume();
    };

    @dispatchProps()
    public getImportFileManager(): IDynamicService<IImportFileManager> | undefined {
        return this.getAppServices().editableServiceLayer?.importFileManager;
    }

    @dispatchProps()
    public getLiveDropdownDynamicService(): IDynamicService<ILiveDropdownService> | undefined {
        return this.getAppServices().viewableServiceLayer?.liveDropDownService;
    }

    @dispatchProps()
    public getHistoryManager(): IHistoryManager | undefined {
        return this.getAppServices().editableServiceLayer?.historyManager;
    }

    @dispatchProps()
    public getCanvasService() {
        return this.getAppServices().viewableServiceLayer.canvasService;
    }

    @dispatchProps()
    public getImageUploadDynamicService(): IDynamicService<IImageUploadService> | undefined {
        return this.getAppServices().editableServiceLayer?.imageUploadService;
    }

    /**
     * 退出演示态
     * @param pageIndex 退出演示态后 focus 到指定页
     */
    private playEnd = (pageIndex?: number) => {
        this.props.handleEndPlay?.(pageIndex);
    };

    /**
     * 调用上层wrapper的play进行播放
     * @param isFromCurrent
     * @param laserTurnOn
     * @param remotePlay
     * @param withPresenterView 是否同时打开 演讲者(备注)视图**独立弹窗**
     */
    private play = (isFromCurrent, laserTurnOn, remotePlay, withPresenterView, mobileRemoteControl) => {
        CommonTips.getInstance().pause();
        // 隐藏快捷浮层
        import(/* webpackChunkName: "service" */ '../../../../../frame/components/quick-setting/quick-setting-manager').then((module) => {
            const QuickSettingManager = module.default;
            QuickSettingManager.getInstance().hide();
        })
            .catch((error) => {
                window.log.error(`[loadChunk-error]${error}`);
            });
        if (this.props.handlePlay) {
            TimeRecord.start(TDWActionType.PLAYER_ENTER, { from: -1 });
            const playerPara = {
                data: {
                    presentation: this.getAppServices().getUiDataService().presentation,
                },
                currentPage: isFromCurrent
                    ? this.getAppServices().viewableServiceLayer.selectionService.currentSlideIndex
                    : this.getAppServices().getUiDataService().presentation.getStartPageIndex(),
                currentFragment: 0,
                laserTurnOn,
                remotePlay,
                withPresenterView,
                mobileRemoteControl,
            };
            try {
                this.props.handlePlay(playerPara);
            } catch (e) {
                window.log.error(e);
            }
        }
    };

    /**
     * 检查是否为动画引起的操作
     * @param mutationsArr
     * @returns boolean
     */
    private checkIfAnimOperation = (mutationsArr): boolean => {
        if (!mutationsArr?.length) {
            return false;
        }
        const animMutations = [
            MutationType.AddAnimation,
            MutationType.RemoveAnimation,
            MutationType.SetAnimationProperties,
            MutationType.MoveAnimation,
        ];
        let isAnimOperation = false;
        for (let i = 0; i < mutationsArr.length; i++) {
            if (MutationType.RemoveShape === mutationsArr[i]) {
                // 只要有涉及删除元素的操作，不管是否有涉及动画的操作，都结束遍历，返回false
                return false;
            }
            if (animMutations.includes(mutationsArr[i])) {
                // 有动画的操作，且未标记时，置标记为true
                isAnimOperation = true;
            }
        }
        return isAnimOperation;
    };

    private getAppServices(): IPcEditorAppServices {
        return AppServiceProvider.getInstance().getAppServices() as
            IPcEditorAppServices;
    }
}
